import { UTM_PARAM_IDS } from '$lib/global/helper/get-query-parameter';

import psProperties from './v4.ps-properties';

import type { Field } from '$lib/types/Field';

const ANALYTICS_METHODS = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
];

/**
 * Create the window.analytics stub (factory queue pattern) if it doesn't exist.
 * Must be called before any component that uses window.analytics mounts
 * (e.g. ConsentOverlay calls activateIntegrations on mount).
 *
 * Safe to call multiple times — no-ops if the stub already exists.
 * The full initialize() later reuses the same stub.
 */
export const ensureAnalyticsStub = (): void => {
    if (typeof window === 'undefined') return;
    const analytics: any = (window.analytics = window.analytics || []);
    window.analytics_queue = window.analytics_queue || [];
    window.ps_queue = window.ps_queue || [];

    if (!analytics.invoked && !analytics.initialize) {
        analytics.invoked = true;
        analytics.methods = ANALYTICS_METHODS;
        analytics.factory = (method: any) => {
            return (...args: any[]) => {
                args.unshift(method);
                window.analytics_queue.push(args);
                window.ps_queue?.push(JSON.parse(JSON.stringify(args)));
                return analytics;
            };
        };
        for (const method of analytics.methods) {
            analytics[method] = analytics.factory(method);
        }
    }
};

/**
 * Build tracking titles map from page fields.
 * Extracted from Page.svelte to be reusable from page.load functions.
 *
 * `fields` is optional because non-funnel routes have no fields: `/consent-policy`
 * is served from `layoutData.json`, which has no `fields` key (CAP-4251). Only the
 * static base titles are returned in that case.
 */
export const getTrackingTitles = (fields: Field[] = []): Record<string, string> => {
    type TrackingKey =
        | Exclude<(typeof psProperties)[keyof typeof psProperties], 'ps_embed'>
        | (typeof UTM_PARAM_IDS)[number];

    const trackingTitles: Record<TrackingKey, string> = {
        ps_first_seen_at: 'First seen at',
        ps_start_slug: 'Start slug',
        ps_start_page: 'Start page',
        ps_end_slug: 'End slug',
        ps_converted_at: 'Converted at',
        ps_completed_at: 'Completed at',
        ps_closed_at: 'Closed at',
        ps_last_seen_at: 'Last seen at',
        ps_converted: 'Has converted',
        ps_completed: 'Has completed',
        ps_closed: 'Has closed',
        ps_result_page: 'Result page',
        ps_calendly_booked: 'Calendly Booked',
        ps_cal_booked: 'Cal.com Booked',
        ps_ghl_booked: 'GoHighLevel Booked',
        ps_hubspot_booked: 'HubSpot Booked',
        ps_iclosed_booked: 'iClosed Booked',
        ps_cookies: 'Cookies accepted',
        ps_timezone: 'Timezone',
        utm_source: 'UTM Source',
        utm_medium: 'UTM Medium',
        utm_campaign: 'UTM Campaign',
        utm_term: 'UTM Term',
        utm_content: 'UTM Content',
    };

    fields.forEach((field) => {
        if (field.interactionType === 'answers' && field.additional.answers.length > 0) {
            trackingTitles[field.additional.answers[0].fieldName as TrackingKey] = field.title;
        } else {
            field.additional.form.inputs.forEach((input) => {
                trackingTitles[input.fieldName as TrackingKey] = input.title;
            });
        }
    });

    return trackingTitles;
};
